import type { ReactNode } from "react";
import { Pressable, Text, View } from "react-native";

import { Icon } from "../Icon/Icon";
import { IconButton } from "../primitives/IconButton";
import type { UiTheme } from "../theme";
import type { V3Styles } from "./v3Styles";

// One 44pt row with a round's schema line (#336, brief §7.2: «stesso
// componente riga in entrambe le viste»). The round ladder labels it with the
// round (W3) and expands it in place; the «Per round» lens labels it with the
// exercise and opens that exercise. `line` null: the exercise does not run in
// that round, and the row says so in words; with `onAdd` it also offers a
// compact «+» that puts the exercise in that round. The ladder's `badge`
// marks a deload or test round after its line (#336 S2).
export function RoundLineRow({
  theme,
  styles,
  variant,
  label,
  badge,
  tag,
  line,
  offText,
  open,
  onPress,
  a11yLabel,
  onAdd,
  addA11yLabel,
}: {
  theme: UiTheme;
  styles: V3Styles;
  /** The ladder's row expands in place; the lens's row opens the exercise. */
  variant: "ladder" | "lens";
  /** The left column: the round (ladder) or the exercise (lens). */
  label: string;
  /** Ladder only: after the line text, the round's type (RoundTypeBadge). */
  badge?: ReactNode;
  /** Under the exercise's name in the lens: its superset. */
  tag?: string | null;
  line: string | null;
  /** What a null line reads as. */
  offText: string;
  /** The ladder's expanded round. */
  open?: boolean;
  /** Absent: read only, still reachable by a screen reader. */
  onPress?: () => void;
  a11yLabel: string;
  /** Off rows only: put the exercise in this round. */
  onAdd?: () => void;
  addA11yLabel?: string;
}) {
  const p = theme.palette;
  const add = line === null && onAdd !== undefined;
  // Only a ladder row that can open is an expander.
  const expander = variant === "ladder" && onPress !== undefined;
  const icon = !onPress ? "minus" : !expander ? "chevron-right" : open ? "chevron-up" : "chevron-down";
  const body = (
    <>
      {variant === "ladder" ? (
        <Text style={styles.round} numberOfLines={1}>
          {label}
        </Text>
      ) : (
        <View style={styles.lensName}>
          <Text style={styles.lensNameText} numberOfLines={2}>
            {label}
          </Text>
          {tag ? <Text style={styles.tag}>{tag}</Text> : null}
        </View>
      )}
      {variant === "ladder" && badge ? (
        // The line text starts at the same x on every row; the badge follows
        // it and wraps below only when it does not fit.
        <View style={styles.roundBody}>
          <Text style={line === null ? styles.roundOffInline : styles.roundLineInline}>{line ?? offText}</Text>
          {badge}
        </View>
      ) : (
        <Text style={line === null ? styles.roundOff : styles.roundLine}>{line ?? offText}</Text>
      )}
      {add ? null : <Icon name={icon} size={theme.typography.sizes.base} color={p.ink3} />}
    </>
  );
  // The «+» is a sibling of the row's own press, never nested in it.
  return (
    <View style={[styles.ladderRow, open ? styles.ladderRowOpen : null, tag ? styles.rowGrouped : null]}>
      {onPress ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={a11yLabel}
          accessibilityState={expander ? { expanded: !!open } : undefined}
          onPress={onPress}
          style={styles.ladderPress}
        >
          {body}
        </Pressable>
      ) : (
        // Read only, still one stop for a screen reader. aria-disabled as
        // well: react-native-web drops accessibilityState.
        <View
          accessible
          accessibilityRole="button"
          accessibilityLabel={a11yLabel}
          accessibilityState={{ disabled: true }}
          aria-disabled
          style={styles.ladderPress}
        >
          {body}
        </View>
      )}
      {add ? (
        <IconButton theme={theme} name="plus" accessibilityLabel={addA11yLabel ?? ""} onPress={onAdd} />
      ) : null}
    </View>
  );
}
